import { IconButton, Typography, BoxProps, Stack, TypographyProps } from '@mui/material';
import { useEffect, useMemo, useState, useRef } from 'react';
import palette from 'theme/palette';
import Icon from '@/units/Icon';
import { formatDate, getSunday } from './utils';

const scrollThreshold = 50;
const minScrollTime = 300;

type Props = {
  startDate?: Date;
  value?: [Date, Date];
  onDateChange?: (range: [Date, Date]) => void;
  textProps?: TypographyProps;
} & BoxProps;

/**
 * Calculates the start and end dates of a week containing the given date.
 * Week always starts on Sunday and ends on Saturday.
 *
 * @param {Date} [date] - The date to calculate the week range from. Defaults to current date
 * @returns {[Date, Date]} A tuple containing the start date (Sunday) and end date (Saturday) of the week
 *
 * @example
 * ```tsx
 * // Get current week range
 * const [start, end] = getWeekRange();
 *
 * // Get week range for specific date
 * const [start, end] = getWeekRange(new Date('2024-01-15'));
 * // start will be 2024-01-14 (Sunday)
 * // end will be 2024-01-20 (Saturday)
 * ```
 */
const getWeekRange = (date?: Date): [Date, Date] => {
  const start = getSunday(date ?? new Date());
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return [start, end];
};

const isInDateRange = (weekRange: [Date, Date], date: Date = new Date()) => {
  return date >= weekRange[0] && date <= weekRange[1];
};

/**
 * A controlled/uncontrolled component that displays and manages week-based date ranges.
 * Allows users to navigate between weeks using either arrow buttons or mouse wheel scroll.
 *
 * The component can work in two modes:
 * 1. Controlled - when `value` prop is provided
 * 2. Uncontrolled - when managing internal state
 *
 * @component
 * @example
 * ```tsx
 * // Uncontrolled usage
 * <WeekSwitch onDateChange={(range) => console.log(range)} />
 *
 * // Controlled usage
 * const [range, setRange] = useState(WeekSwitch.getWeekRange());
 * // ...
 * <WeekSwitch
 *   value={range}
 *   onDateChange={setRange}
 * />
 * ```
 *
 * @property {Date} [startDate] - Initial date to calculate the week from. Defaults to current date
 * @property {[Date, Date]} [value] - Controlled value representing start and end of the week
 * @property {(range: [Date, Date]) => void} [onDateChange] - Callback fired when the date range changes
 *
 * @note The component has a static `getWeekRange` helper function attached to it.
 * This function is exposed for external use cases where you need to calculate a week range
 * without rendering the component. Common use case is initializing state or pre-calculating
 * ranges for validation.
 */
const WeekSwitch = ({ onDateChange, startDate, value, ...props }: Props) => {
  const [internalWeekStart, setInternalWeekStart] = useState(getSunday(startDate ?? new Date()));
  const scrollAccumulator = useRef<number>(0);
  const lastScrollTime = useRef<number>(0);

  // Use value if provided, otherwise use internal state
  const weekStart = value ? value[0] : internalWeekStart;
  const weekEnd = useMemo(() => {
    if (value) return value[1];
    const weekRange = getWeekRange(internalWeekStart);

    return weekRange[1];
  }, [value, internalWeekStart]);

  useEffect(() => {
    if (!value) {
      onDateChange?.([internalWeekStart, weekEnd]);
    }
  }, [internalWeekStart, weekEnd, onDateChange, value]);

  /**
   * Changes week start date by specified number of weeks to move forward or backward
   * @param weeksCount Count of weeks to move. Can be negative meaning move to previous N weeks
   */
  const handleWeekChange = (weeksCount: number) => {
    const days = weeksCount * 7;
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + days);
    const newWeekStart = getSunday(newDate);

    if (value) {
      // If controlled, only notify parent
      const weekRange = getWeekRange(newWeekStart);
      onDateChange?.(weekRange);
    } else {
      setInternalWeekStart(newWeekStart);
    }
  };

  /**
   * Handles wheel event to change week start date with configurable sensitivity
   */
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    const now = Date.now();
    if (now - lastScrollTime.current < minScrollTime) {
      return;
    }

    scrollAccumulator.current += Math.abs(e.deltaY);

    // Only change week if accumulated scroll exceeds threshold
    if (scrollAccumulator.current >= scrollThreshold) {
      if (e.deltaY < 0) {
        handleWeekChange(-1);
      } else if (e.deltaY > 0) {
        handleWeekChange(1);
      }

      // Reset accumulator and update last scroll time
      scrollAccumulator.current = 0;
      lastScrollTime.current = now;
    }
  };

  return (
    <Stack
      direction="row"
      gap="8px"
      {...props}
      sx={{
        boxSizing: 'border-box',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '18px',
        border: '1px solid #d0d5dd',
        borderRadius: '8px',
        height: '44px',
        width: '100%',
        cursor: 'ns-resize',
        backgroundColor: '#fff',
        ...props.sx,
      }}
      onWheel={handleWheel}
    >
      <IconButton
        onClick={() => handleWeekChange(-1)}
        sx={{
          minWidth: '24px',
          width: '24px',
          height: '24px',
          padding: 0,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
            '& svg': {
              color: palette.light.primary.main,
            },
          },
          '&:active': {
            backgroundColor: 'transparent',
            '& svg': {
              color: palette.light.primary.dark,
            },
          },
        }}
      >
        <Icon.ChevronLeft sx={{ fontSize: '18px', color: palette.light.grey[600], transition: 'color 0.2s' }} />
      </IconButton>

      <Typography
        {...props.textProps}
        sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '14px', lineHeight: '24px', textAlign: 'center', flex: 1, color: '#344054', ...props.textProps?.sx }}
      >
        Week: {formatDate(weekStart)} - {formatDate(weekEnd)}
      </Typography>

      <IconButton
        onClick={() => handleWeekChange(1)}
        sx={{
          minWidth: '24px',
          width: '24px',
          height: '24px',
          padding: 0,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
            '& svg': {
              color: palette.light.primary.main,
            },
          },
          '&:active': {
            backgroundColor: 'transparent',
            '& svg': {
              color: palette.light.primary.dark,
            },
          },
        }}
      >
        <Icon.ChevronRight sx={{ fontSize: '18px', color: palette.light.grey[600], transition: 'color 0.2s' }} />
      </IconButton>
    </Stack>
  );
};

WeekSwitch.getWeekRange = getWeekRange;
WeekSwitch.isInDateRange = isInDateRange;

export { WeekSwitch };
